/**
 * Shipping Capability Helper (frontend)
 *
 * The legacy team-page permission map gated /shipping and /claims
 * behind a single `shipping` boolean. The redesign splits that into
 * four narrower capabilities so a merchant can grant a teammate
 * read-only access without also granting label purchase, claim filing,
 * or settings edit.
 *
 *   shipping:view      - read shipping orders, filters, history
 *   shipping:purchase  - buy labels, buy insurance, refund labels
 *   shipping:claims    - file claims; void insurance (server also allows void with purchase or claims)
 *   shipping:settings  - edit shipping address, carrier prefs, auto-insure
 *
 * This helper is the single source of truth on the frontend. Use it
 * instead of reaching into accessPermissions or root.permissions
 * directly. The backend mirror lives at
 * `papaya/src/api/middleware/shippingAccess.ts` - frontend hiding is
 * UX, server enforcement is the real boundary.
 *
 * Backward compatibility (must match the backend rules):
 *   - admin → always allowed
 *   - root.permissions contains the requested shipping:* string → allowed
 *   - team accessPermissions explicitly blocks the capability key with
 *     `false` → denied (this is how the team page UI will gate
 *     individual capabilities once shipped)
 *   - team accessPermissions explicitly blocks the legacy 'shipping'
 *     page key with `false` → denied for ALL four capabilities
 *   - otherwise → allowed (legacy/grandfathered)
 *
 * Risk visibility is intentionally separate; see canViewRisk.ts.
 */

import { useSelector } from 'react-redux'

import { PERMISSIONS } from './Router.permissionRoute'

export type ShippingCapability =
  | typeof PERMISSIONS.SHIPPING_VIEW
  | typeof PERMISSIONS.SHIPPING_PURCHASE
  | typeof PERMISSIONS.SHIPPING_CLAIMS
  | typeof PERMISSIONS.SHIPPING_SETTINGS

const ALL_SHIPPING_CAPS: readonly ShippingCapability[] = [
  PERMISSIONS.SHIPPING_VIEW,
  PERMISSIONS.SHIPPING_PURCHASE,
  PERMISSIONS.SHIPPING_CLAIMS,
  PERMISSIONS.SHIPPING_SETTINGS,
]

export interface ShippingAccessContext {
  admin?: boolean | null
  /** Staff RBAC permission strings (`root.permissions`). */
  permissions?: string[] | null
  /** Team page access map keyed by canonical id. May contain 'shipping' (legacy) or 'shipping:purchase' (new). */
  accessPermissions?: Record<string, boolean> | null | undefined
}

const isExplicitlyDenied = (
  accessPermissions: Record<string, boolean> | null | undefined,
  key: string,
): boolean => accessPermissions?.[key] === false

/**
 * Pure check. Use this when you already have permissions in scope
 * (props, unit tests, selectors).
 */
export const canShip = (
  ctx: ShippingAccessContext,
  capability: ShippingCapability = PERMISSIONS.SHIPPING_VIEW,
): boolean => {
  if (ctx?.admin) return true

  // Explicit deny on the new capability key takes precedence.
  if (isExplicitlyDenied(ctx?.accessPermissions, capability)) return false

  // Explicit deny on the legacy page key denies ALL four capabilities.
  if (isExplicitlyDenied(ctx?.accessPermissions, 'shipping')) return false

  // Explicit grant via staff RBAC.
  const perms = ctx?.permissions || []
  if (perms.includes(capability)) return true

  // Legacy/grandfathered: missing/undefined keys default to allow,
  // matching the existing `teamPageAccessAllowed` semantics.
  return true
}

/** Convenience: same check from the underlying store fields. */
export const canShipFromState = (
  admin: boolean | null | undefined,
  permissions: string[] | null | undefined,
  accessPermissions: Record<string, boolean> | null | undefined,
  capability: ShippingCapability = PERMISSIONS.SHIPPING_VIEW,
): boolean =>
  canShip(
    {
      admin: !!admin,
      permissions: permissions || [],
      accessPermissions: accessPermissions || undefined,
    },
    capability,
  )

/**
 * Hook for functional components. Returns `true` if the current user
 * has the requested shipping capability.
 *
 * Usage:
 *   const canBuy = useCanShip(PERMISSIONS.SHIPPING_PURCHASE)
 *   if (!canBuy) return <ReadOnlyHint />
 */
export const useCanShip = (
  capability: ShippingCapability = PERMISSIONS.SHIPPING_VIEW,
): boolean => {
  const admin = useSelector((state: any) => !!state.root?.admin)
  const permissions: string[] = useSelector(
    (state: any) => state.root?.permissions || [],
  )
  const accessPermissions = useSelector(
    (state: any) => state.root?.accessPermissions || undefined,
  )
  return canShipFromState(admin, permissions, accessPermissions, capability)
}

/**
 * Hook returning all four capability flags in one shot. Useful for
 * containers that render multiple gated controls (purchase button,
 * claim button, settings tab) on the same screen.
 */
export const useShippingCapabilities = (): {
  canViewShipping: boolean
  canPurchaseShipping: boolean
  canFileClaims: boolean
  canEditShippingSettings: boolean
} => {
  const admin = useSelector((state: any) => !!state.root?.admin)
  const permissions: string[] = useSelector(
    (state: any) => state.root?.permissions || [],
  )
  const accessPermissions = useSelector(
    (state: any) => state.root?.accessPermissions || undefined,
  )
  return {
    canViewShipping: canShipFromState(admin, permissions, accessPermissions, PERMISSIONS.SHIPPING_VIEW),
    canPurchaseShipping: canShipFromState(admin, permissions, accessPermissions, PERMISSIONS.SHIPPING_PURCHASE),
    canFileClaims: canShipFromState(admin, permissions, accessPermissions, PERMISSIONS.SHIPPING_CLAIMS),
    canEditShippingSettings: canShipFromState(admin, permissions, accessPermissions, PERMISSIONS.SHIPPING_SETTINGS),
  }
}

export const SHIPPING_CAPABILITY_KEYS = ALL_SHIPPING_CAPS

export default canShip
